/**
 * src/services/reports.api.ts 의 짝.
 *
 * 리포트는 만드는 시점에 집계를 굳히므로 여기서도 payload 를 만들어 db.reports 에 박아 둔다 —
 * 뒤에 Daily mock 을 고쳐도 이미 만든 리포트의 숫자는 바뀌지 않는다. 중복은 막지 않는다:
 * 같은 기간을 열 번 뽑으면 열 건이 남는다.
 *
 * 리포트는 모두가 본다. 금액과 근거 Daily 만 볼 때 관리자에게만 붙는다.
 */
import { HttpResponse, http } from "msw";
import type {
  Report,
  ReportComment,
  ReportCommentInput,
  ReportCreateInput,
  ReportRow,
} from "@/types";
import { buildReport, spanDays } from "../data/reports";
import { db } from "../db";
import { fail, latency, requireUser } from "../http";

const BASE = "/api/v1/reports";
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_PERIOD_DAYS = 400;
let seq = 0;

const isAdmin = (userId: string) => db.users.find((u) => u.id === userId)?.role === "admin";

/**
 * 보는 사람에 맞춘 사본 — 백엔드처럼 금액과 근거 Daily(사람별 한 일)는 관리자에게만 간다. 리포트 자체는
 * 모두가 본다. AI 가 쓴 글(story)은 요약 파이프라인과 함께 생길 것이라 백엔드에 없다 — 내보내지 않는다.
 */
function filtered(userId: string, report: Report): Report {
  const { story: _story, ...rest } = report;
  if (isAdmin(userId)) return { ...rest, money_visible: true };
  return {
    ...rest,
    money_visible: false,
    payload: report.payload && {
      ...report.payload,
      projects: report.payload.projects.map((section) => ({
        ...section,
        members: section.members.map(({ descriptions: _text, ...member }) => member),
      })),
    },
  };
}

/** 목록 한 줄 — 백엔드처럼 집계와 코멘트는 빼고 머리만 싣는다. */
const row = (report: Report): ReportRow => {
  const {
    payload: _payload,
    money_visible: _money,
    comments: _comments,
    story: _story,
    custom_prompt: _prompt,
    ...rest
  } = report;
  return rest;
};

export const reportHandlers = [
  http.get(`${BASE}/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.reports
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .map((r) => row(filtered(user.id, r)));
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const report = db.reports.find((r) => r.id === params.id);
    if (!report) return fail(404, "없는 리포트입니다.");
    return HttpResponse.json(filtered(user.id, report));
  }),

  http.post(`${BASE}/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const input = (await request.json()) as ReportCreateInput;
    if (!DATE.test(input.period_from) || !DATE.test(input.period_to))
      return fail(422, "기간을 확인해 주세요.");
    if (input.period_to < input.period_from) return fail(422, "기간의 끝이 시작보다 빨라요.");
    if (spanDays(input.period_from, input.period_to) > MAX_PERIOD_DAYS)
      return fail(422, "기간이 너무 길어요.");
    if (input.project_ids.some((id) => !db.projects.some((p) => p.id === id)))
      return fail(422, "없는 프로젝트입니다.");
    // 비우면 그 시점의 진행 중 프로젝트를 담는 전사 리포트가 된다.
    const projectIds = input.project_ids.length
      ? input.project_ids
      : db.projects.filter((p) => p.status !== "done" && p.status !== "cancelled").map((p) => p.id);
    const now = new Date().toISOString();
    const report: Report = buildReport(
      db,
      {
        id: `report-new-${++seq}`,
        requested_by: user.id,
        schedule_id: null,
        status: "ready",
        period_from: input.period_from,
        period_to: input.period_to,
        generated_at: now,
        failure_reason: "",
        created_at: now,
        comments: [],
      },
      projectIds
    );
    db.reports.push(report);
    return HttpResponse.json(filtered(user.id, report), { status: 201 });
  }),

  http.post(`${BASE}/:id/comments/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const report = db.reports.find((r) => r.id === params.id);
    if (!report) return fail(404, "없는 리포트입니다.");
    const input = (await request.json()) as ReportCommentInput;
    if (!input.body?.trim()) return fail(422, "내용을 적어 주세요.");
    if (input.parent_id) {
      const parent = report.comments.find((c) => c.id === input.parent_id);
      if (!parent) return fail(404, "없는 코멘트입니다.");
      if (parent.parent_id) return fail(422, "답글에는 답글을 달 수 없어요.");
    }
    const comment: ReportComment = {
      id: `report-comment-new-${++seq}`,
      project_id: input.project_id ?? null,
      parent_id: input.parent_id ?? null,
      author_id: user.id,
      body: input.body.trim(),
      created_at: new Date().toISOString(),
      updated_at: null,
    };
    report.comments.push(comment);
    return HttpResponse.json(comment, { status: 201 });
  }),

  http.delete(`${BASE}/:id/comments/:commentId/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const report = db.reports.find((r) => r.id === params.id);
    if (!report) return fail(404, "없는 리포트입니다.");
    const index = report.comments.findIndex((c) => c.id === params.commentId);
    if (index < 0) return fail(404, "없는 코멘트입니다.");
    if (report.comments[index].author_id !== user.id && !isAdmin(user.id))
      return fail(403, "자기 코멘트만 지울 수 있어요.");
    report.comments.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),
];
